import { useState, useMemo } from 'react';
import {
  sourceColors, segmentColors, durumColors, katilimColors,
  ALL_DURUMLAR, ALL_KATILIM_DURUMLARI, ALL_KAYNAKLAR,
  type Lead, type Kaynak, type Durum, type LansmanKatilimDurum,
} from '../../../mocks/leads';

type SortKey = 'ad_soyad' | 'olusturma_tarihi' | 'durum' | 'kaynak';

interface AllApplicantsProps {
  leads: Lead[];
  onOpenDrawer: (lead: Lead) => void;
  onBulkUpdate: (ids: string[], patch: Partial<Lead>) => void;
}

export default function AllApplicants({ leads, onOpenDrawer, onBulkUpdate }: AllApplicantsProps) {
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState<Kaynak | 'Tümü'>('Tümü');
  const [filterDurum, setFilterDurum] = useState<Durum | 'Tümü'>('Tümü');
  const [filterKatilim, setFilterKatilim] = useState<LansmanKatilimDurum | 'Tümü'>('Tümü');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('olusturma_tarihi');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [bulkDurum, setBulkDurum] = useState<Durum | ''>('');
  const perPage = 10;

  const filtered = useMemo(() => {
    let data = [...leads];
    if (filterSource !== 'Tümü') data = data.filter((l) => l.kaynak === filterSource);
    if (filterDurum !== 'Tümü') data = data.filter((l) => l.durum === filterDurum);
    if (filterKatilim !== 'Tümü') data = data.filter((l) => l.lansman_katilim_durumu === filterKatilim);
    if (dateFrom) data = data.filter((l) => l.olusturma_tarihi >= dateFrom);
    if (dateTo) data = data.filter((l) => l.olusturma_tarihi <= dateTo + 'T23:59:59');
    if (search) {
      const q = search.toLowerCase();
      data = data.filter((l) => l.ad_soyad.toLowerCase().includes(q) || l.telefon.includes(q) || l.id.toLowerCase().includes(q));
    }
    data.sort((a, b) => {
      const cmp = String(a[sortKey]).localeCompare(String(b[sortKey]), 'tr');
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return data;
  }, [leads, search, filterSource, filterDurum, filterKatilim, dateFrom, dateTo, sortKey, sortDir]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);
  const allChecked = paginated.length > 0 && paginated.every((l) => selected.has(l.id));

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
    setPage(1);
  }

  function toggleAll() {
    if (allChecked) setSelected((s) => { const n = new Set(s); paginated.forEach((l) => n.delete(l.id)); return n; });
    else setSelected((s) => { const n = new Set(s); paginated.forEach((l) => n.add(l.id)); return n; });
  }

  function toggleOne(id: string) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  function handleBulkStatusUpdate() {
    if (!bulkDurum || selected.size === 0) return;
    onBulkUpdate([...selected], { durum: bulkDurum as Durum, guncelleme_tarihi: new Date().toISOString() });
    setSelected(new Set());
    setBulkDurum('');
  }

async function handleBulkInvite() {
  if (selected.size === 0) return;

  const selectedLeads = leads.filter((l) => selected.has(l.id));

  for (const lead of selectedLeads) {

    const phone = lead.telefon.replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Merhaba ${lead.ad_soyad}, franchise lansmanımıza davet edildiniz! 🚀`
    );

    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');

    // 🔥 ID temizle
    const id = lead.id.replace("LEAD-", "");

    await fetch("/api/davet_gonder.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: `id=${id}`
    });
  }

  onBulkUpdate([...selected], {
    davetGonderildi: true,
    lansman_katilim_durumu: 'Davet Gönderildi',
    davet_gonderim_tarihi: new Date().toISOString(),
    guncelleme_tarihi: new Date().toISOString()
  });

  setSelected(new Set());
}
  function SortIcon({ k }: { k: SortKey }) {
    if (sortKey !== k) return <i className="ri-expand-up-down-line text-white/20 ml-1" />;
    return <i className={`ri-arrow-${sortDir === 'asc' ? 'up' : 'down'}-line text-amber-400 ml-1`} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-white">Tüm Başvurular</h1>
          <p className="text-white/30 text-sm mt-0.5">{filtered.length} başvuru</p>
        </div>
        <button onClick={() => setShowFilters((s) => !s)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${showFilters ? 'bg-amber-500/20 text-amber-400 border-amber-500/40' : 'bg-white/[0.04] text-white/40 border-white/[0.08] hover:text-white/70'}`}>
          <i className="ri-filter-3-line" />Filtreler {showFilters ? '▲' : '▼'}
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Ad, telefon veya ID ara..."
          className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-amber-500/50" />
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-white/30 text-[10px] font-semibold uppercase tracking-widest mb-1.5">Kaynak</p>
              <select value={filterSource} onChange={(e) => { setFilterSource(e.target.value as Kaynak | 'Tümü'); setPage(1); }}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs focus:outline-none cursor-pointer">
                <option value="Tümü" className="bg-[#0f1420]">Tüm Kaynaklar</option>
                {ALL_KAYNAKLAR.map((k) => <option key={k} value={k} className="bg-[#0f1420]">{k}</option>)}
              </select>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-semibold uppercase tracking-widest mb-1.5">Durum</p>
              <select value={filterDurum} onChange={(e) => { setFilterDurum(e.target.value as Durum | 'Tümü'); setPage(1); }}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs focus:outline-none cursor-pointer">
                <option value="Tümü" className="bg-[#0f1420]">Tüm Durumlar</option>
                {ALL_DURUMLAR.map((d) => <option key={d} value={d} className="bg-[#0f1420]">{d}</option>)}
              </select>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-semibold uppercase tracking-widest mb-1.5">Katılım Durumu</p>
              <select value={filterKatilim} onChange={(e) => { setFilterKatilim(e.target.value as LansmanKatilimDurum | 'Tümü'); setPage(1); }}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-xs focus:outline-none cursor-pointer">
                <option value="Tümü" className="bg-[#0f1420]">Tüm Katılım</option>
                {ALL_KATILIM_DURUMLARI.map((k) => <option key={k} value={k} className="bg-[#0f1420]">{k}</option>)}
              </select>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-semibold uppercase tracking-widest mb-1.5">Tarih Aralığı</p>
              <div className="flex gap-2">
                <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                  className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-2 text-white text-xs focus:outline-none" />
                <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                  className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-2 py-2 text-white text-xs focus:outline-none" />
              </div>
            </div>
          </div>
          <button onClick={() => { setFilterSource('Tümü'); setFilterDurum('Tümü'); setFilterKatilim('Tümü'); setDateFrom(''); setDateTo(''); setSearch(''); setPage(1); }}
            className="text-white/30 hover:text-white/60 text-xs cursor-pointer transition-colors">
            <i className="ri-refresh-line mr-1" />Filtreleri Temizle
          </button>
        </div>
      )}

      {/* Bulk Action Bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/25 flex-wrap">
          <span className="text-amber-400 text-xs font-semibold">{selected.size} seçili</span>
          <div className="flex gap-2 flex-1 flex-wrap">
            <button onClick={handleBulkInvite}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25d366]/15 hover:bg-[#25d366]/25 text-[#25d366] text-xs font-semibold cursor-pointer whitespace-nowrap transition-all">
              <i className="ri-whatsapp-line" />Toplu Davet Gönder
            </button>
            <select value={bulkDurum} onChange={(e) => setBulkDurum(e.target.value as Durum | '')}
              className="bg-white/[0.06] border border-white/[0.10] rounded-lg px-3 py-1.5 text-white/60 text-xs focus:outline-none cursor-pointer">
              <option value="" className="bg-[#0f1420]">Toplu Durum Seç...</option>
              {ALL_DURUMLAR.map((d) => <option key={d} value={d} className="bg-[#0f1420]">{d}</option>)}
            </select>
            {bulkDurum && (
              <button onClick={handleBulkStatusUpdate}
                className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-semibold cursor-pointer whitespace-nowrap transition-all">
                Uygula
              </button>
            )}
            <button onClick={() => setSelected(new Set())} className="ml-auto text-white/25 hover:text-white/50 text-xs cursor-pointer whitespace-nowrap transition-colors">
              İptal
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl bg-white/[0.03] border border-white/[0.07] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                <th className="px-4 py-3">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-amber-400 cursor-pointer w-3.5 h-3.5" />
                </th>
                {[
                  { label: 'ID', k: null },
                  { label: 'Ad Soyad', k: 'ad_soyad' as SortKey },
                  { label: 'Telefon', k: null },
                  { label: 'Kaynak', k: 'kaynak' as SortKey },
                  { label: 'Durum', k: 'durum' as SortKey },
                  { label: 'Katılım', k: null },
                  { label: 'Tarih', k: 'olusturma_tarihi' as SortKey },
                  { label: '', k: null },
                ].map(({ label, k }) => (
                  <th key={label} onClick={() => k && toggleSort(k)}
                    className={`text-left px-4 py-3 text-white/30 text-[11px] font-semibold uppercase tracking-wider ${k ? 'cursor-pointer hover:text-white/60 select-none' : ''}`}>
                    {label}{k && <SortIcon k={k} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-12 text-center text-white/20 text-sm">
                  <i className="ri-inbox-line text-3xl block mb-2" />Sonuç bulunamadı
                </td></tr>
              ) : paginated.map((lead) => (
                <tr key={lead.id} className={`border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors cursor-pointer ${selected.has(lead.id) ? 'bg-amber-500/[0.04]' : ''}`}
                  onClick={() => onOpenDrawer(lead)}>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(lead.id)} onChange={() => toggleOne(lead.id)} className="accent-amber-400 cursor-pointer w-3.5 h-3.5" />
                  </td>
                  <td className="px-4 py-3 text-white/25 text-xs font-mono">{lead.id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                        <span className="text-amber-400 text-xs font-bold">{lead.ad_soyad.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="text-white font-medium text-xs">{lead.ad_soyad}</p>
                        {lead.manuel_mi && <span className="text-[9px] text-amber-400/60">Manuel</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/40 text-xs font-mono">{lead.telefon}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: (sourceColors[lead.kaynak] || '#6b7280') + '22', color: sourceColors[lead.kaynak] || '#6b7280' }}>{lead.kaynak}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: (durumColors[lead.durum] || '#94a3b8') + '22', color: durumColors[lead.durum] || '#94a3b8' }}>{lead.durum}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: (katilimColors[lead.lansman_katilim_durumu] || '#94a3b8') + '22', color: katilimColors[lead.lansman_katilim_durumu] || '#94a3b8' }}>{lead.lansman_katilim_durumu}</span>
                  </td>
                  <td className="px-4 py-3 text-white/25 text-xs">{new Date(lead.olusturma_tarihi).toLocaleDateString('tr-TR')}</td>
                  <td className="px-4 py-3">
                    <button onClick={(e) => { e.stopPropagation(); onOpenDrawer(lead); }}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] hover:bg-amber-500/15 text-white/30 hover:text-amber-400 transition-all cursor-pointer">
                      <i className="ri-eye-line text-sm" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
          <span className="text-white/25 text-xs">{Math.min((page - 1) * perPage + 1, filtered.length)}–{Math.min(page * perPage, filtered.length)} / {filtered.length}</span>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => setPage(p)}
                className={`w-7 h-7 rounded text-xs font-semibold cursor-pointer transition-all ${page === p ? 'bg-amber-500/30 text-amber-400' : 'text-white/30 hover:text-white/60 hover:bg-white/[0.04]'}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
